import { useApp } from '@libs/core';
import { TWidget } from '@libs/types';
import Persona, { ClientOptions, InquiryError } from 'persona';
import { useEffect, useEffectEvent, useRef } from 'react';
import { TKycPersonaCredentials, TKycProviderData } from '../../../types/data';
import { PERSONA_OUTCOME, PersonaResult } from './personaOutcome';
import { useKycOutcome } from './useKycOutcome';

type TPersonaProviderProps = {
    widget: TWidget<TKycProviderData>;
    credentials: TKycPersonaCredentials;
};

export const PersonaProvider = ({ widget, credentials }: TPersonaProviderProps) => {
    const { language } = useApp();
    const { fireOutcome } = useKycOutcome(widget);
    const lastInquiryIdRef = useRef<string | null>(credentials.inquiryId ?? null);

    const createClient = useEffectEvent(() => {
        const common: Partial<ClientOptions> = {
            language: credentials.locale ?? language,
            onReady: () => client.open(),
            onEvent: (_name, metadata) => {
                if (typeof metadata?.inquiryId === 'string') lastInquiryIdRef.current = metadata.inquiryId;
            },
            onComplete: ({ inquiryId, status }) => {
                if (status !== PersonaResult.COMPLETED && status !== PersonaResult.FAILED) {
                    console.warn(`Persona onComplete: unexpected status "${status}" mapped to onError/verificationRejected`);
                }
                const result = status === PersonaResult.COMPLETED ? PERSONA_OUTCOME[PersonaResult.COMPLETED] : PERSONA_OUTCOME[PersonaResult.FAILED];
                fireOutcome({ ...result, providerStatus: status, inquiryId: inquiryId ?? credentials.inquiryId ?? null });
            },
            onCancel: ({ inquiryId }) => {
                fireOutcome({ ...PERSONA_OUTCOME[PersonaResult.CANCEL], providerStatus: null, inquiryId: inquiryId ?? credentials.inquiryId ?? null });
            },
            onError: (error: InquiryError) => {
                console.error('Persona SDK error:', error);
                fireOutcome({ ...PERSONA_OUTCOME[PersonaResult.ERROR], providerStatus: null, inquiryId: lastInquiryIdRef.current });
            },
        };

        const options: ClientOptions = credentials.inquiryId
            ? { ...common, inquiryId: credentials.inquiryId, sessionToken: credentials.sessionToken }
            : {
                  ...common,
                  templateId: credentials.templateId,
                  ...(credentials.environmentId ? { environmentId: credentials.environmentId } : { environment: credentials.environment ?? 'production' }),
                  referenceId: credentials.referenceId,
                  fields: {
                      ...(credentials.firstName ? { nameFirst: credentials.firstName } : {}),
                      ...(credentials.lastName ? { nameLast: credentials.lastName } : {}),
                  },
              };

        const client = new Persona.Client(options);
        return client;
    });

    useEffect(() => {
        const client = createClient();
        return () => client.destroy();
    }, []);

    return null;
};
